import React from 'react';
import { useNavigate } from 'react-router-dom';
import { PlusIcon } from '@heroicons/react/24/solid';

export interface Budget {
  id: string;
  name: string;
  icon: string;
  amount: number;
  spent: number;
  budgetType?: 'Expense' | 'Savings' | 'Expense Fund';
  savingsGoal?: 'Target Goal' | 'Monthly Goal';
  targetDate?: string;
  expensePeriodValue?: number;
  expensePeriodUnit?: 'Months' | 'Years';
  availableAmount?: number;
  expenseAmount?: number;
  savingsAmount?: number;
}

interface BudgetItemProps {
  budget: Budget;
  onAddClick: (budget: Budget) => void;
  selectedYear?: number;
  selectedMonth?: number;
  onBudgetClick?: () => void;
}

export function BudgetItem({ budget, onAddClick, selectedYear, selectedMonth, onBudgetClick }: BudgetItemProps) {
  const navigate = useNavigate();
  
  // Calculate remaining/available amount based on budget type
  let remaining = 0;
  let percentageRemaining = 0;
  let isOverspent = false;
  
  if (budget.budgetType === 'Expense Fund') {
    // For Expense Fund budgets on home page, use availableAmount from backend
    // which is calculated as all-time savings minus all-time expenses
    remaining = budget.availableAmount !== undefined ? budget.availableAmount : 0;
    
    // For percentage calculation, use the total period amount as the "budget"
    percentageRemaining = budget.amount > 0 ? (remaining / budget.amount) * 100 : 0;

    if (remaining < 0) {
      isOverspent = true;
    }
    else {
      isOverspent = false;
    }
  } else {
    // Regular budget calculation
    remaining = budget.amount - budget.spent;
    percentageRemaining = budget.amount > 0 ? (remaining / budget.amount) * 100 : 0;
    isOverspent = budget.spent > budget.amount;
  }
  
  // Determine progress bar color
  let progressColor = 'bg-green-500';
  if (budget.budgetType === 'Expense Fund') {
    // For Expense Fund budgets, use red if negative, green if positive
    progressColor = remaining < 0 ? 'bg-red-500' : 'bg-green-500';
  } else if (isOverspent) {
    progressColor = 'bg-red-500';
  }
  else if (percentageRemaining <= 15) {
    progressColor = 'bg-yellow-500';
  }

  // Render icon - handle both Font Awesome classes and emoji
  const renderIcon = () => {
    if (budget.icon.startsWith('fas fa-') || budget.icon.startsWith('far fa-') || budget.icon.startsWith('fab fa-')) {
      return <i className={budget.icon}></i>;
    }
    return budget.icon; // Return as-is for emoji
  };

  const handleBudgetClick = (e: React.MouseEvent) => {
    // Don't navigate if clicking the add button
    if ((e.target as HTMLElement).closest('button')) {
      return;
    }

    // Save scroll position before navigating
    if (onBudgetClick) {
      onBudgetClick();
    }

    // Build URL with year and month parameters if provided
    let url = `/budget/${budget.id}`;
    const params = new URLSearchParams();
    if (selectedYear !== undefined) {
      params.set('year', selectedYear.toString());
    }
    if (selectedMonth !== undefined) {
      params.set('month', selectedMonth.toString());
    }
    if (params.toString()) {
      url += `?${params.toString()}`;
    }

    navigate(url);
  };

  return (
    <div 
      className="bg-white rounded-xl shadow relative overflow-hidden cursor-pointer hover:shadow-md transition-shadow"
      onClick={handleBudgetClick}
    >
      <div className="flex items-center p-4 pb-3">
        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-[#F8F9FA] mr-4">
          <div className="text-sm">{renderIcon()}</div>
        </div>
        <div className="flex-1">
          <div className="font-semibold text-lg">{budget.name}</div>
          <div className="text-base font-semibold">
            {budget.budgetType === 'Expense Fund' ? (
              <span className={isOverspent ? 'text-red-600' : 'text-gray-600'}>
                ${remaining.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} available
              </span>
            ) : isOverspent ? (
              <span className="text-red-600">
                ${Math.abs(remaining).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} overspent
              </span>
            ) : (
              <span className={remaining === 0 ? 'text-gray-600' : percentageRemaining <= 15 ? 'text-yellow-600' : 'text-gray-600'}>
                ${remaining.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} left <span className="text-gray-500 font-normal">of ${budget.amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              </span>
            )}
          </div>
        </div>
        <button
          className="ml-4 flex items-center justify-center w-12 h-12 rounded-full bg-[#F8F9FA] hover:bg-gray-200 transition"
          onClick={(e) => {
            e.stopPropagation();
            onAddClick(budget);
          }}
          aria-label={`Add transaction to ${budget.name}`}
        >
          <PlusIcon className="w-7 h-7 text-black" />
        </button>
      </div>
      {/* Built-in progress bar at bottom */}
      <div className="h-1.5 bg-gray-300 relative">
        <div
          className={`h-full transition-all ${progressColor}`}
          style={{ 
            width: `${Math.min(100, Math.max(0, percentageRemaining))}%`,
            position: 'absolute',
            left: '0',
            top: '0',
            borderRadius: '3px 3px 3px 3px'
          }}
        />
      </div>
    </div>
  );
} 